// src/app/components/brand/upmode-home.tsx
//
// Upmode public homepage (AI Index probe flow). Recreates the
// "Upmode Home.dc.html" design reference in the codebase, wired to the REAL
// brand-monitoring analysis: a visitor types a brand, Start analysis opens the
// monitoring WebSocket, the hero shows a live reasoning trace during the ~1 min
// wait, and the real completion event flips the hero to a fast "done" handoff
// that ejects into the dashboard.
//
// Design tokens come from theme.css; the few raw rgba washes are taken verbatim
// from the product surfaces (antenna-perception-overview) per the handoff.
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Sparkles, Check, Clock, X, ArrowUpRight, ArrowRight,
  SlidersHorizontal, History, Send, MessageSquare, Loader2,
} from "lucide-react";

import {
  MODEL_CATALOG, PICKER_MODEL_IDS, DEFAULT_LLM_MODELS, LIVE_MODEL_IDS,
  getModelColor, type ProviderId,
} from "@/app/constants/models";
import { buildWsUrl } from "@/api/url-route";
import { openBrandMonitoringSocket, type BrandMonitoringWsMessage, type ConfirmedScopePayload } from "@/app/services/brand/brand-monitoring-ws";
import { apiFetch, getStoredToken, AuthExpiredError, notifyAuthExpired } from "@/api/api-fetch";
import { AntennaHistory } from "../antenna/antenna-history";
import { AIAssistantSidebar } from "../ai-assistant-sidebar";
import { Orb } from "../ai-sidebar/components/orb";
import { ScopePicker, type ScopePickerHandle, type ResolvedCandidate, type ConfirmedScope } from "./scope-picker";
import { SiteHeader } from "../navigation/site-header";

interface UpmodeHomeProps {
  onConfigure: (brand: string, models: string[]) => void;
  onLoadFromHistory: (payload: { brandName: string; selectedModels: string[]; antennaData: any }) => void;
  isSidebarCollapsed?: boolean;
  onSidebarToggle?: (collapsed: boolean) => void;
  onLogout?: () => void;
  onNavigate?: (page: string) => void;
}

type Phase = "idle" | "resolving" | "picking" | "analyzing" | "done";

const TRACE_TPL = [
  "Querying recommendation prompts that mention {b}",
  "{m} is describing {b} unprompted",
  "Comparing {b} against the alternatives AI names",
  "Checking which sources {m} cites about {b}",
  "Flagging outdated or missing facts about {b}",
  "Scoring sentiment and recommendation likelihood",
  "Mapping {b} across prompt clusters",
  "Synthesizing your perception snapshot",
];

function tierFor(score: number): string {
  return score >= 90 ? "Outstanding" : score >= 80 ? "Excellent" : score >= 70 ? "Good" : score >= 60 ? "Fair" : "Developing";
}
function indexFromResult(result: any): number {
  const raw = result?.perceptionData?.overallScore;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.round(Math.max(0, Math.min(100, n)));
  return 0;
}

const ORB_GRADIENT = "linear-gradient(135deg,var(--spectral-violet) 0%,var(--spectral-violet) 62%,var(--signal-blue) 155%)";
const WASH = "linear-gradient(135deg, rgba(126,95,224,0.06), rgba(0,201,196,0.045) 58%, var(--card))";

// Ambient "AI is working" texture behind the analyzing card. A faint, blurred,
// edge-masked mono log stream that trickles new lines in: the subtle carry-over
// of the old loading screen's terminal background, dialed way down so it never
// competes with the reasoning trace. Honors prefers-reduced-motion (renders a
// static frame, no ticking).
function CodeStream({ lines }: { lines: string[] }) {
  const [rows, setRows] = useState<string[]>(() => [...lines, ...lines, ...lines, ...lines]);
  const idx = useRef(0);
  const pool = useRef(lines);
  useEffect(() => { pool.current = lines; }, [lines]);
  useEffect(() => {
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const t = setInterval(() => {
      setRows((prev) => [...prev, pool.current[idx.current % pool.current.length] ?? ""].slice(-200));
      idx.current++;
    }, 110);
    return () => clearInterval(t);
  }, []);
  return (
    <div aria-hidden style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0, opacity: 0.09, filter: "blur(0.5px)", maskImage: "linear-gradient(180deg, transparent, #000 12%, #000 88%, transparent)", WebkitMaskImage: "linear-gradient(180deg, transparent, #000 12%, #000 88%, transparent)" }}>
      <div style={{ width: "min(56%, 720px)", height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: "0 0 0 40px", fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.7, color: "var(--tx-strong)", whiteSpace: "pre", maskImage: "linear-gradient(90deg, #000 62%, transparent)", WebkitMaskImage: "linear-gradient(90deg, #000 62%, transparent)" }}>
        {rows.map((l, i) => <div key={i}>{l}</div>)}
      </div>
    </div>
  );
}

export function UpmodeHome({
  onLoadFromHistory,
  onLogout,
  onNavigate,
}: UpmodeHomeProps) {
  const [brand, setBrand] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [models, setModels] = useState<ProviderId[]>(() => [...DEFAULT_LLM_MODELS]);
  const [showModels, setShowModels] = useState(false);
  const [traceStep, setTraceStep] = useState(0);
  const [gaugeVal, setGaugeVal] = useState(0);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [introDismissed, setIntroDismissed] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySelectingId, setHistorySelectingId] = useState<number | null>(null);
  const [finalResult, setFinalResult] = useState<any>(null);
  const [realOpen, setRealOpen] = useState(false);
  const [realQ, setRealQ] = useState<{ text: string; ts: number } | null>(null);
  const [assistantInput, setAssistantInput] = useState("");

  const wsRef = useRef<WebSocket | null>(null);
  const traceTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const gaugeTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    wsRef.current?.close();
    if (traceTimer.current) clearInterval(traceTimer.current);
    if (gaugeTimer.current) clearInterval(gaugeTimer.current);
  }, []);

  const brandName = brand.trim() || "your brand";
  const est = Math.max(20, models.length * 15);
  const modelsLabel = `${models.length} AI ${models.length === 1 ? "model" : "models"} · ~${est}s`;
  const selected = MODEL_CATALOG.filter((m) => models.includes(m.id));

  const streamLines = useMemo(() => {
    const ms = selected.map((m) => m.label);
    const b = brandName;
    return [
      `→ GET /prompts?brand="${b}" cluster=recommendation`,
      `  dispatch ${ms[0] ?? "model"} · temp=0.0 · web=on`,
      `  parse citations[] … 14 sources`,
      `  ${ms[1] ?? "model"} mentions "${b}" unprompted`,
      `  sentiment=+0.62 · recommend_likelihood=0.48`,
      `  diff baseline ↔ current … Δ pending`,
      `  rank("${b}") = 3 of 9 in category`,
      `  ${ms[2] ?? "model"} cites competitor set [4]`,
      `  flag: outdated_fact @ line 22`,
      `  embed prompt-cluster map … 1.2k dims`,
      `  aggregate per-model scores …`,
      `✓ snapshot frame ready`,
    ];
  }, [brandName, selected]);

  const animateGauge = (target: number) => {
    if (gaugeTimer.current) clearInterval(gaugeTimer.current);
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setGaugeVal(target); return; }
    const t0 = performance.now();
    gaugeTimer.current = setInterval(() => {
      const t = Math.min(1, (performance.now() - t0) / 780);
      const e = 1 - Math.pow(1 - t, 3);
      if (t >= 1) { if (gaugeTimer.current) clearInterval(gaugeTimer.current); setGaugeVal(target); }
      else setGaugeVal(target * e);
    }, 16);
  };

  const stopAnalysis = () => {
    wsRef.current?.close();
    wsRef.current = null;
    if (traceTimer.current) { clearInterval(traceTimer.current); traceTimer.current = null; }
  };

  // ── Scope resolution (the scope picker) ──────────────────────────────────
  // start() now resolves the typed term first (1.5s budget). Ambiguous terms
  // (multi-category brands, or multiple organizations sharing the name) open
  // the picker; everything else proceeds exactly as before with zero added
  // clicks. Resolution failure or timeout NEVER blocks analysis.
  const [pickerCandidates, setPickerCandidates] = useState<ResolvedCandidate[]>([]);
  const [pickerButtonLabel, setPickerButtonLabel] = useState("Start analysis");
  // Canonical brand name from the resolver. Differs from the typed text when
  // the user misspelled ("titalist" -> "Titleist"); the picker then doubles as
  // the did-you-mean confirmation and the run uses the corrected name.
  const [canonicalBrand, setCanonicalBrand] = useState<string | null>(null);
  // Set when the resolver "corrected" the typed name: the raw typed text,
  // offered in the picker as an explicit keep-what-I-typed row (launch brands
  // and novel names must never be silently renamed to a famous lookalike).
  const [pickerKeepTyped, setPickerKeepTyped] = useState<string | null>(null);
  const [pickerUnknown, setPickerUnknown] = useState(false);
  const pickerRef = useRef<ScopePickerHandle | null>(null);
  const pillWrapRef = useRef<HTMLDivElement | null>(null);
  const confirmedScopeRef = useRef<ConfirmedScopePayload | null>(null);
  // Monotonic id for resolve attempts. Editing the brand or dismissing the
  // picker bumps it, which invalidates any in-flight resolution so a stale
  // .then() can never start an analysis the user backed out of.
  const resolveRunIdRef = useRef(0);

  // Back to editing: close the picker / abandon resolution WITHOUT running
  // anything. This is the escape hatch the flow was missing: clicking or
  // typing in the brand field, or clicking outside the popover, lands here.
  const cancelToIdle = () => {
    resolveRunIdRef.current += 1;
    setPickerCandidates([]);
    setPickerKeepTyped(null);
    setPickerUnknown(false);
    setPickerButtonLabel("Start analysis");
    setCanonicalBrand(null);
    setPhase("idle");
  };

  const start = () => {
    const b = brand.trim();
    const liveModels = models.filter((m) => LIVE_MODEL_IDS.includes(m));
    if (b.length < 2 || liveModels.length === 0) return;

    setPhase("resolving");
    setCanonicalBrand(null); // fresh term, fresh resolution
    const myRun = ++resolveRunIdRef.current;
    // Conservative rule: WAIT for the resolver rather than racing it and
    // silently assuming on timeout (cold calls measured at 8-12s; the old 8s
    // budget was eating the picker). The pill shows "Checking..." the whole
    // time and typing in the brand field cancels instantly, so a long wait is
    // always escapable. The 30s cap only guards against a hung request; a
    // resolver ERROR still proceeds unscoped (a dead resolver must not brick
    // analysis), and that path is logged server-side.
    const budget = new Promise<null>((res) => setTimeout(() => res(null), 30000));
    const lookup = apiFetch(`/entity/resolve?q=${encodeURIComponent(b)}`).catch(() => null);

    Promise.race([lookup, budget]).then((resolved: any) => {
      // Stale guard: the user edited the brand or backed out while this
      // resolution was in flight. Do nothing; their intent changed.
      if (resolveRunIdRef.current !== myRun) return;
      if (!resolved || typeof resolved !== "object") {
        beginAnalysis(null);
        return;
      }
      const candidates: ResolvedCandidate[] = Array.isArray(resolved.candidates) ? resolved.candidates : [];
      const entityCount = candidates.filter((c) => c.kind === "entity").length;
      const categoryCount = candidates.filter((c) => c.kind === "category").length;
      const productCount = candidates.filter((c) => c.kind === "product").length;
      const canonical: string = (resolved.canonical_name || b).trim();
      const corrected: boolean = !!resolved.corrected && canonical.toLowerCase() !== b.toLowerCase();
      setCanonicalBrand(canonical);

      // Conservative rule: ask, never assume. The picker opens for ANY
      // ambiguity: same-name organizations, multiple categories, multiple
      // notable products/sub-brands, a spelling correction, or a
      // fuzzy-matched onboarded client. The only zero-click path is a brand
      // the resolver affirms is genuinely simple: one line, no famous
      // sub-products, no name twins. Confirming costs one Enter.
      if (entityCount >= 2 || categoryCount >= 2 || productCount >= 2 || corrected || resolved.known_match) {
        setPickerCandidates(candidates);
        setPickerKeepTyped(corrected ? b : null);
        setPickerUnknown(false);
        setPhase("picking");
        return;
      }
      // The AIs found essentially nothing: a new or launching brand. Open the
      // new-brand page so a website can anchor the measurement from day one
      // (or the user chooses to analyze the bare name deliberately).
      if (entityCount + categoryCount + productCount === 0 && !resolved.anchor) {
        setPickerCandidates([]);
        setPickerKeepTyped(null);
        setPickerUnknown(true);
        setPhase("picking");
        return;
      }
      // Unambiguous. If the resolver knew this brand from intake, carry the
      // identity anchor into the run.
      const scope: ConfirmedScopePayload | null = resolved.anchor
        ? { type: "entity", name: canonical, anchor: resolved.anchor }
        : null;
      beginAnalysis(scope);
    });
  };

  const beginAnalysis = (scope: ConfirmedScopePayload | null, nameOverride?: string) => {
    const b = (nameOverride || canonicalBrand || brand).trim();
    const liveModels = models.filter((m) => LIVE_MODEL_IDS.includes(m));
    if (b.length < 2 || liveModels.length === 0) return;
    if (b !== brand.trim()) setBrand(b); // reflect the corrected spelling in the input

    confirmedScopeRef.current = scope;
    localStorage.setItem("bm_brand", b);
    localStorage.setItem("bm_models", JSON.stringify(liveModels));
    if (scope) localStorage.setItem("bm_scope", JSON.stringify(scope));
    else localStorage.removeItem("bm_scope");
    setFinalResult(null);
    setPhase("analyzing");
    setTraceStep(0);
    setGaugeVal(0);

    // Cosmetic reasoning trace during the real (~1 min) wait. Advances steps but
    // holds on the last one until the real completion event arrives.
    if (traceTimer.current) clearInterval(traceTimer.current);
    traceTimer.current = setInterval(() => {
      setTraceStep((s) => Math.min(s + 1, TRACE_TPL.length));
    }, 680);

    const token = getStoredToken();
    if (!token) { notifyAuthExpired("/ws/brand-monitoring"); throw new AuthExpiredError("/ws/brand-monitoring"); }

    wsRef.current = openBrandMonitoringSocket({
      baseWsUrl: buildWsUrl(),
      token,
      brand: b,
      models: liveModels,
      scope: confirmedScopeRef.current,
      onEvent: (msg: BrandMonitoringWsMessage) => {
        if (msg.type === "final_result") {
          const data = (msg as any).data;
          const runId = data?.runId;
          if (runId !== undefined && runId !== null) localStorage.setItem("bm_brand_id", String(runId));
          localStorage.setItem("bm_last_result", JSON.stringify(data));
          stopAnalysis();
          setFinalResult(data);
          setTraceStep(TRACE_TPL.length);
          setPhase("done");
          animateGauge(indexFromResult(data) || 0);
          return;
        }
        if (msg.type === "error") {
          // Surface the failure by returning to idle; the brand stays typed.
          console.error("Monitoring error:", (msg as any).message);
          stopAnalysis();
          setPhase("idle");
        }
      },
    });
  };

  const reset = () => {
    stopAnalysis();
    if (gaugeTimer.current) { clearInterval(gaugeTimer.current); gaugeTimer.current = null; }
    setPhase("idle");
    setTraceStep(0);
    setGaugeVal(0);
    setFinalResult(null);
  };

  const openDashboard = () => {
    if (!finalResult) return;
    onLoadFromHistory({
      brandName: finalResult.brandName ?? brand.trim(),
      selectedModels: models as string[],
      antennaData: finalResult,
    });
  };

  // Hand off to the real Upmode AI Assistant. An optional question is sent
  // through verbatim so the launched assistant opens straight into that thread.
  const launchAssistant = (q?: string) => {
    setAssistantOpen(false);
    setIntroDismissed(true);
    setAssistantInput("");
    setRealQ(q ? { text: q, ts: Date.now() } : null);
    setRealOpen(true);
  };

  const toggleModel = (id: ProviderId) => {
    if (!LIVE_MODEL_IDS.includes(id)) return;
    setModels((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const handleSelectHistory = async (runId: number) => {
    setHistorySelectingId(runId);
    try {
      const resp = await apiFetch<{ id: number; brandName: string; selectedModels: string[]; createdAt: string | null; data: any }>(
        `/monitoring/runs/${runId}`,
      );
      const antennaData = resp.data || {};
      antennaData.runId = resp.id;
      localStorage.setItem("bm_brand_id", String(resp.id));
      localStorage.setItem("bm_brand", resp.brandName);
      localStorage.setItem("bm_models", JSON.stringify(resp.selectedModels || []));
      localStorage.setItem("bm_last_result", JSON.stringify(antennaData));
      setHistoryOpen(false);
      onLoadFromHistory({ brandName: resp.brandName, selectedModels: resp.selectedModels || [], antennaData });
    } catch (e) {
      console.error("Failed to load run details:", e);
    } finally {
      setHistorySelectingId(null);
    }
  };

  // Derived for analyzing trace
  const vis = Math.min(traceStep, TRACE_TPL.length);
  const score = Math.round(gaugeVal);
  const doneIndex = indexFromResult(finalResult);
  const tier = tierFor(doneIndex);
  const progressPct = Math.min(95, Math.round((traceStep / (TRACE_TPL.length + 1)) * 100));

  return (
    // data-kit turns on the kit base layer for this subtree. The hero itself
    // is a recreation of "Upmode Home.dc.html", which is a WEBSITE reference
    // rather than a product specimen — F13's `$doesNotTransfer` note is
    // explicitly about keeping the website's sizes out of the product — so its
    // type scale stays as drawn. See design/MISSING.md.
    <div data-kit style={{ minHeight: "100vh", background: "var(--s-canvas)", color: "var(--tx-default)", position: "relative" }}>
      <style>{`
        @keyframes upPulse{0%,100%{box-shadow:0 0 0 0 var(--alpha-teal-20);}50%{box-shadow:0 0 0 6px rgba(0,201,196,0);}}
        @keyframes upBreathe{0%,100%{box-shadow:0 10px 30px rgba(126,95,224,0.28);}50%{box-shadow:0 12px 44px rgba(126,95,224,0.50);}}
        @keyframes upIntro{from{opacity:0;transform:translateY(10px) scale(.98);}to{opacity:1;transform:translateY(0) scale(1);}}
        @keyframes upFade{from{opacity:0;transform:translateY(10px);}to{opacity:1;transform:translateY(0);}}
        @keyframes upHalo{0%{transform:scale(1);opacity:0.5;}70%,100%{transform:scale(1.65);opacity:0;}}
        @media (prefers-reduced-motion: reduce){.up-anim{animation-duration:0.001ms !important;animation-iteration-count:1 !important;}}
      `}</style>

      <AntennaHistory
        isOpen={historyOpen}
        onClose={() => setHistoryOpen(false)}
        onSelect={handleSelectHistory}
        selectingId={historySelectingId}
      />

      {/* ===== Header (shared with the admin sections) ===== */}
      <SiteHeader currentPage="brand-search" onNavigate={onNavigate} onLogout={onLogout} />

      {/* ===== Hero ===== */}
      <div style={{ position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: "radial-gradient(58% 60% at 16% -4%, rgba(126,95,224,0.07), transparent 70%), radial-gradient(54% 56% at 86% 4%, rgba(40,169,210,0.07), transparent 70%), radial-gradient(46% 50% at 50% -8%, rgba(0,201,196,0.06), transparent 72%)" }} />
        {phase === "analyzing" && <CodeStream lines={streamLines} />}
        <div style={{ position: "relative", maxWidth: 1280, margin: "0 auto", padding: "76px 40px 72px", minHeight: "calc(100vh - 64px)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{ maxWidth: 960, margin: "0 auto", textAlign: "center" }}>
            <h1 style={{ margin: 0, fontSize: "clamp(38px, 6vw, 66px)", lineHeight: 1.02, letterSpacing: "-0.038em", fontWeight: 700, color: "var(--tx-strong)" }}>
              AI already has an opinion.<br />Start shaping it here.
            </h1>
          </div>

          <div style={{ maxWidth: 900, width: "100%", margin: "64px auto 0" }}>

            {(phase === "idle" || phase === "resolving" || phase === "picking") && (
              <div>
                <div ref={pillWrapRef} style={{ position: "relative" }}>
                <div className="flex" style={{ gap: 10, padding: 9, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 15, boxShadow: "var(--elevation-md)", flexWrap: "wrap" }}>
                  <div className="flex items-center" style={{ flex: 1, gap: 11, padding: "0 8px 0 14px", minWidth: 220 }}>
                    <Search style={{ width: 20, height: 20, color: "var(--muted-foreground)", flexShrink: 0 }} />
                    <input
                      value={brand}
                      onChange={(e) => {
                        setBrand(e.target.value);
                        // Typing while checking or picking means "let me edit":
                        // abandon the in-flight resolution / close the picker.
                        if (phase !== "idle") cancelToIdle();
                      }}
                      onFocus={() => { if (phase === "picking") cancelToIdle(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" && phase === "idle") start(); }}
                      placeholder="Enter a brand like Patagonia, a product, or paste a link"
                      maxLength={40}
                      style={{ flex: 1, height: 54, border: "none", outline: "none", background: "transparent", fontSize: 17, fontFamily: "inherit", color: "var(--foreground)", minWidth: 0 }}
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (phase === "idle") start();
                      else if (phase === "picking") pickerRef.current?.confirmActive();
                    }}
                    disabled={phase === "resolving"}
                    className="inline-flex items-center"
                    style={{ gap: 8, height: 54, padding: "0 26px", borderRadius: 11, background: "var(--s-inverse)", color: "var(--tx-inverse)", border: "none", cursor: phase === "resolving" ? "default" : "pointer", fontSize: 16, fontWeight: 600, whiteSpace: "nowrap", opacity: phase === "resolving" ? 0.85 : 1 }}
                  >
                    {phase === "resolving" ? (
                      <>
                        <Loader2 className="animate-spin" style={{ width: 17, height: 17 }} />
                        Checking...
                      </>
                    ) : phase === "picking" ? (
                      <>
                        {pickerButtonLabel}
                        <ArrowRight style={{ width: 17, height: 17 }} />
                      </>
                    ) : (
                      <>
                        Start analysis
                        <ArrowRight style={{ width: 17, height: 17 }} />
                      </>
                    )}
                  </button>
                </div>
                {phase === "picking" && (
                  <ScopePicker
                    ref={pickerRef}
                    brand={(canonicalBrand || brand).trim()}
                    anchorEl={pillWrapRef.current}
                    candidates={pickerCandidates}
                    keepTypedName={pickerKeepTyped}
                    unknown={pickerUnknown}
                    onConfirm={(scope: ConfirmedScope) =>
                      // A whole-brand confirmation names the brand to run
                      // (corrected canonical OR the kept-as-typed name).
                      beginAnalysis(
                        scope as ConfirmedScopePayload,
                        scope.type === "brand" ? scope.name : undefined,
                      )}
                    onWholeBrand={() => beginAnalysis(null, brand.trim())}
                    onHighlight={setPickerButtonLabel}
                    onDismiss={cancelToIdle}
                    resolveScopes={async (entityName: string) => {
                      // Per-entity scope lookup: each disambiguated
                      // organization gets its own categories/products.
                      try {
                        const r: any = await apiFetch(`/entity/resolve?q=${encodeURIComponent(entityName)}`);
                        return Array.isArray(r?.candidates) ? r.candidates : [];
                      } catch { return []; }
                    }}
                  />
                )}
                </div>

                <div className="flex items-center" style={{ justifyContent: "space-between", gap: 12, marginTop: 15, flexWrap: "wrap" }}>
                  <div className="flex items-center" style={{ gap: 13, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>Probing</span>
                    {selected.map((m) => (
                      <span key={m.id} className="inline-flex items-center" style={{ gap: 6, fontSize: 13.5, fontWeight: 500, color: "var(--foreground)" }}>
                        <span style={{ width: 8, height: 8, borderRadius: 9999, flexShrink: 0, background: getModelColor(m.id) }} />
                        {m.label}
                      </span>
                    ))}
                  </div>
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <button onClick={() => setShowModels((s) => !s)} className="inline-flex items-center" style={{ gap: 7, height: 34, padding: "0 13px", borderRadius: 8, background: "transparent", border: "1px solid var(--border)", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>
                      <SlidersHorizontal style={{ width: 15, height: 15 }} /> Customize
                    </button>
                    <button onClick={() => setHistoryOpen(true)} aria-label="History" className="inline-flex items-center justify-center" style={{ width: 34, height: 34, borderRadius: 8, background: "transparent", border: "1px solid var(--border)", cursor: "pointer", color: "var(--muted-foreground)" }}>
                      <History style={{ width: 15, height: 15 }} />
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 12, textAlign: "center", fontSize: 13, color: "var(--muted-foreground)" }}>
                  {modelsLabel} &nbsp;·&nbsp; Builds your initial Upmode AI Index
                </div>

                {showModels && (
                  <div className="up-anim" style={{ marginTop: 16, padding: 22, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--elevation-sm)", animation: "upFade 200ms var(--ease-standard)" }}>
                    <div className="flex items-start" style={{ justifyContent: "space-between", gap: 14, marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted-foreground)", marginBottom: 5 }}>Optional · advanced</div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: "var(--tx-strong)" }}>Choose which models to probe</div>
                        <div style={{ fontSize: 13, color: "var(--muted-foreground)", marginTop: 3, maxWidth: 430 }}>We pre-select the most-queried models. Add more to widen coverage and surface perception variance.</div>
                      </div>
                      <button onClick={() => setShowModels(false)} className="inline-flex items-center justify-center" style={{ flexShrink: 0, width: 30, height: 30, borderRadius: 8, background: "transparent", border: "1px solid var(--border)", cursor: "pointer", color: "var(--muted-foreground)" }}>
                        <X style={{ width: 15, height: 15 }} />
                      </button>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                      {PICKER_MODEL_IDS.map((id) => {
                        const m = MODEL_CATALOG.find((x) => x.id === id)!;
                        const on = models.includes(id);
                        return (
                          <button
                            key={id}
                            onClick={() => toggleModel(id)}
                            className="inline-flex items-center"
                            style={{ gap: 11, width: "100%", padding: "13px 15px", borderRadius: 10, cursor: "pointer", textAlign: "left", background: on ? "var(--alpha-teal-5)" : "var(--card)", border: `1px solid ${on ? "var(--alpha-teal-50)" : "var(--border)"}`, boxShadow: on ? "var(--shadow-accent-ring)" : "none", transition: "all 150ms var(--ease-standard)" }}
                          >
                            <span className="inline-flex items-center justify-center" style={{ width: 20, height: 20, borderRadius: 6, flexShrink: 0, color: "#fff", background: on ? "var(--alpha-teal)" : "transparent", border: `1px solid ${on ? "var(--alpha-teal)" : "var(--border)"}` }}>
                              {on && <Check style={{ width: 12, height: 12 }} />}
                            </span>
                            <span style={{ width: 9, height: 9, borderRadius: 9999, flexShrink: 0, background: getModelColor(id) }} />
                            <span className="flex flex-col" style={{ lineHeight: 1.3, minWidth: 0 }}>
                              <span style={{ fontSize: 14, fontWeight: 600, color: "var(--tx-strong)" }}>{m.label}</span>
                              <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>{m.provider}</span>
                            </span>
                            {m.recommended && (
                              <span style={{ marginLeft: "auto", padding: "2px 9px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: "var(--alpha-teal-10)", color: "var(--alpha-teal)" }}>Recommended</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {phase === "analyzing" && (
              <div className="up-anim" style={{ padding: "24px 26px 20px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "var(--elevation-md)", textAlign: "left", animation: "upFade 220ms var(--ease-standard)" }}>
                <div className="flex items-center" style={{ gap: 12 }}>
                  <span className="inline-flex items-center justify-center" style={{ position: "relative", width: 34, height: 34, borderRadius: 9, background: ORB_GRADIENT, flexShrink: 0 }}>
                    <Sparkles style={{ width: 18, height: 18, color: "#fff" }} />
                    <span className="up-anim" style={{ position: "absolute", inset: 0, borderRadius: 9, border: "1.5px solid var(--spectral-violet)", animation: "upHalo 1.8s var(--ease-standard) infinite" }} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--spectral-violet)" }}>Reading brand data</div>
                    <div style={{ fontSize: 17, fontWeight: 600, color: "var(--tx-strong)" }}>Reasoning about {brandName}</div>
                  </div>
                  <span className="inline-flex items-center" style={{ gap: 6, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--spectral-violet)" }}>
                    <span className="up-anim" style={{ width: 7, height: 7, borderRadius: 9999, background: "var(--spectral-violet)", animation: "upPulse 1.4s var(--ease-standard) infinite" }} />
                    Live
                  </span>
                </div>
                <div style={{ marginTop: 16, height: 6, borderRadius: 9999, background: "var(--muted)", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${progressPct}%`, borderRadius: 9999, background: "linear-gradient(90deg, var(--signal-blue), var(--alpha-teal))", transition: "width 500ms var(--ease-standard)" }} />
                </div>
                <div style={{ marginTop: 16, display: "flex", flexDirection: "column" }}>
                  {Array.from({ length: vis }).map((_, i) => {
                    const m = selected[i % Math.max(1, selected.length)];
                    const done = traceStep > TRACE_TPL.length ? true : i < traceStep - 1;
                    const text = TRACE_TPL[i].replace("{b}", brandName).replace("{m}", m ? m.label : "AI");
                    return (
                      <div key={i} className="up-anim flex items-center" style={{ gap: 10, padding: "6px 0", animation: "upFade 240ms var(--ease-standard)" }}>
                        <span className="inline-flex items-center justify-center" style={{ width: 18, height: 18, flexShrink: 0, color: "var(--alpha-teal)" }}>
                          {done ? (
                            <Check style={{ width: 16, height: 16 }} />
                          ) : (
                            <span style={{ width: 9, height: 9, borderRadius: 9999, background: m ? getModelColor(m.id) : "var(--alpha-teal)", boxShadow: "0 0 0 4px rgba(0,201,196,0.10)" }} />
                          )}
                        </span>
                        <span style={{ flex: 1, minWidth: 0, fontSize: 14.5, color: done ? "var(--muted-foreground)" : "var(--foreground)", fontWeight: done ? 400 : 500 }}>{text}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex items-center" style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)", gap: 8, fontSize: 13, color: "var(--muted-foreground)" }}>
                  <Clock style={{ width: 14, height: 14, flexShrink: 0 }} />
                  This usually takes about a minute. We'll take you straight in when it's ready.
                </div>
              </div>
            )}

            {phase === "done" && (
              <div className="up-anim flex items-center" style={{ gap: 22, padding: "22px 26px", background: "linear-gradient(135deg, rgba(126,95,224,0.05), rgba(0,201,196,0.04) 58%, var(--card))", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "var(--elevation-md)", textAlign: "left", animation: "upFade 240ms var(--ease-standard)", flexWrap: "wrap" }}>
                <span className="inline-flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 9999, background: "var(--alpha-teal-10)", color: "var(--alpha-teal)", flexShrink: 0 }}>
                  <Check style={{ width: 24, height: 24 }} />
                </span>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.01em", color: "var(--tx-strong)" }}>Your analysis for {brandName} is ready</div>
                  <div className="flex items-center" style={{ marginTop: 4, gap: 8, fontSize: 13.5, color: "var(--muted-foreground)", flexWrap: "wrap" }}>
                    <span>Upmode AI Index</span>
                    {doneIndex > 0 && <span style={{ fontWeight: 700, color: "var(--alpha-teal)", fontVariantNumeric: "tabular-nums" }}>{score}</span>}
                    {doneIndex > 0 && <span style={{ opacity: 0.5 }}>·</span>}
                    {doneIndex > 0 && <span>{tier}</span>}
                    <span style={{ opacity: 0.5 }}>·</span>
                    <span>{modelsLabel}</span>
                  </div>
                </div>
                <div className="flex items-center" style={{ gap: 8, flexShrink: 0 }}>
                  <button onClick={openDashboard} className="inline-flex items-center" style={{ gap: 8, height: 46, padding: "0 24px", borderRadius: 11, background: "var(--s-inverse)", color: "var(--tx-inverse)", border: "none", cursor: "pointer", fontSize: 15, fontWeight: 600 }}>
                    Open your dashboard <ArrowUpRight style={{ width: 16, height: 16 }} />
                  </button>
                  <button onClick={reset} className="inline-flex items-center" style={{ height: 46, padding: "0 14px", borderRadius: 11, background: "transparent", color: "var(--muted-foreground)", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
                    Run another
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* ===== How Upmode works ===== */}
      <div id="how" style={{ borderTop: "1px solid var(--border)", background: "var(--background)" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "80px 40px 88px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>How Upmode works</div>
          <h2 style={{ margin: "10px 0 0", fontSize: "clamp(26px, 4vw, 40px)", lineHeight: 1.14, letterSpacing: "-0.022em", fontWeight: 600, color: "var(--tx-strong)" }}>Decode how AI reasons about you. Shape what it says next.</h2>

          <div style={{ marginTop: 42, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {/* 01 Probe */}
            <div style={{ padding: 20, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--elevation-sm)" }}>
              <div style={{ height: 152, borderRadius: 10, background: WASH, border: "1px solid var(--border)", padding: "15px 16px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div className="flex items-center" style={{ gap: 10, marginBottom: 15 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", color: "var(--alpha-teal)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>REASONING PATHS</span>
                  <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>
                <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg viewBox="0 0 300 96" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" fill="none" style={{ display: "block" }}>
                    {/* origin = your brand */}
                    <circle cx="18" cy="48" r="9" stroke="#0AA39E" strokeWidth="1" opacity="0.3" />
                    <circle cx="18" cy="48" r="4.5" fill="#00C9C4" />
                    {/* trunk */}
                    <path d="M27,48 H60" stroke="#0AA39E" strokeWidth="2" strokeLinecap="round" />
                    {/* vertical bus */}
                    <path d="M60,14 V82" stroke="#D6D7DA" strokeWidth="1.5" strokeLinecap="round" />
                    {/* ChatGPT */}
                    <circle cx="60" cy="14" r="2" fill="#00C9C4" />
                    <path d="M60,14 H166" stroke="#00C9C4" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="172" cy="14" r="4" fill="#00C9C4" />
                    <text x="184" y="17.6" className="text-muted-foreground" style={{ fontFamily: "var(--font-mono)" }} fill="currentColor" fontSize="10">ChatGPT</text>
                    {/* Claude */}
                    <circle cx="60" cy="37" r="2" fill="#FF6D66" />
                    <path d="M60,37 H166" stroke="#FF6D66" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="172" cy="37" r="4" fill="#FF6D66" />
                    <text x="184" y="40.6" className="text-muted-foreground" style={{ fontFamily: "var(--font-mono)" }} fill="currentColor" fontSize="10">Claude</text>
                    {/* Gemini */}
                    <circle cx="60" cy="59" r="2" fill="#28A9D2" />
                    <path d="M60,59 H166" stroke="#28A9D2" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="172" cy="59" r="4" fill="#28A9D2" />
                    <text x="184" y="62.6" className="text-muted-foreground" style={{ fontFamily: "var(--font-mono)" }} fill="currentColor" fontSize="10">Gemini</text>
                    {/* Perplexity */}
                    <circle cx="60" cy="82" r="2" fill="#7E5FE0" />
                    <path d="M60,82 H166" stroke="#7E5FE0" strokeWidth="1.8" strokeLinecap="round" />
                    <circle cx="172" cy="82" r="4" fill="#7E5FE0" />
                    <text x="184" y="85.6" className="text-muted-foreground" style={{ fontFamily: "var(--font-mono)" }} fill="currentColor" fontSize="10">Perplexity</text>
                  </svg>
                </div>
              </div>
              <div className="flex items-baseline" style={{ marginTop: 20, gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--alpha-teal)", fontVariantNumeric: "tabular-nums" }}>01</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: "var(--tx-strong)" }}>Probe</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.55, color: "var(--muted-foreground)" }}>Recommendations follow understanding. We show you how clearly AI understands your brand and how that translates into recommending you, or doesn't.</div>
            </div>

            {/* 02 Measure */}
            <div style={{ padding: 20, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--elevation-sm)" }}>
              <div style={{ height: 152, borderRadius: 10, background: WASH, border: "1px solid var(--border)", padding: "15px 16px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div className="flex items-center" style={{ gap: 10, marginBottom: 15 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", color: "var(--alpha-teal)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>PER-MODEL READ</span>
                  <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 14 }}>
                  {[
                    { label: "ChatGPT", color: "var(--alpha-teal)", pct: 74, val: "0.74" },
                    { label: "Claude", color: "var(--infrared-coral)", pct: 42, val: "0.42" },
                    { label: "Gemini", color: "var(--signal-blue)", pct: 61, val: "0.61" },
                  ].map((r) => (
                    <div key={r.label} className="flex items-center" style={{ gap: 9 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 9999, background: r.color, flexShrink: 0 }} />
                      <span style={{ width: 54, flexShrink: 0, fontSize: 10, fontFamily: "var(--font-mono)", color: "var(--muted-foreground)" }}>{r.label}</span>
                      <div style={{ flex: 1, height: 7, borderRadius: 9999, background: "var(--warm-mist)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${r.pct}%`, borderRadius: 9999, background: r.color }} />
                      </div>
                      <span style={{ width: 30, textAlign: "right", fontSize: 11, fontWeight: 700, color: "var(--tx-strong)", fontVariantNumeric: "tabular-nums", fontFamily: "var(--font-mono)" }}>{r.val}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-baseline" style={{ marginTop: 20, gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--signal-blue)", fontVariantNumeric: "tabular-nums" }}>02</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: "var(--tx-strong)" }}>Measure</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.55, color: "var(--muted-foreground)" }}>Where AI misreads or overlooks you, demand goes to someone else. We uncover the openings worth going after to increase the AI funnel.</div>
            </div>

            {/* 03 Shape */}
            <div style={{ padding: 20, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--elevation-sm)" }}>
              <div style={{ height: 152, borderRadius: 10, background: WASH, border: "1px solid var(--border)", padding: "15px 16px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div className="flex items-center" style={{ gap: 10, marginBottom: 15 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: "0.09em", color: "var(--alpha-teal)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>BASE → POST</span>
                  <span style={{ flex: 1, height: 1, background: "var(--border)" }} />
                </div>
                <div className="flex items-center" style={{ flex: 1, gap: 12 }}>
                  <div className="flex flex-col" style={{ flex: 1, padding: 11, borderRadius: 8, background: "var(--card)", border: "1px solid var(--border)", gap: 8 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", fontFamily: "var(--font-mono)", color: "var(--muted-foreground)", marginBottom: 1 }}>BASELINE</span>
                    {[60, 40, 52].map((w, i) => <span key={i} style={{ height: 6, width: `${w}%`, borderRadius: 9999, background: "var(--silver-mist)" }} />)}
                  </div>
                  <div className="flex flex-col items-center" style={{ gap: 3, color: "var(--alpha-teal)", flexShrink: 0 }}>
                    <ArrowUpRight style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, fontFamily: "var(--font-mono)" }}>Δ</span>
                  </div>
                  <div className="flex flex-col" style={{ flex: 1, padding: 11, borderRadius: 8, background: "var(--alpha-teal-5)", border: "1px solid var(--alpha-teal-20)", gap: 8 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", fontFamily: "var(--font-mono)", color: "var(--alpha-teal)", marginBottom: 1 }}>POST</span>
                    {[88, 70, 92].map((w, i) => <span key={i} style={{ height: 6, width: `${w}%`, borderRadius: 9999, background: "var(--alpha-teal)" }} />)}
                  </div>
                </div>
              </div>
              <div className="flex items-baseline" style={{ marginTop: 20, gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--spectral-violet)", fontVariantNumeric: "tabular-nums" }}>03</span>
                <span style={{ fontSize: 20, fontWeight: 600, color: "var(--tx-strong)" }}>Shape</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.55, color: "var(--muted-foreground)" }}>Platform tools to build interventions that change how AI understands your brand, so it recommends you more often.</div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Footer ===== */}
      <div style={{ borderTop: "1px solid var(--border)" }}>
        <div className="flex items-center" style={{ maxWidth: 1280, margin: "0 auto", padding: "24px 40px", justifyContent: "space-between", gap: 16, flexWrap: "wrap", color: "var(--muted-foreground)", fontSize: 13 }}>
          <span>Upmode <span style={{ color: "var(--alpha-teal)" }}>↗</span> · Get recommended by AI more often.</span>
          <span style={{ letterSpacing: "0.02em" }}>Probe · Measure · Simulate · Act · Re-probe</span>
        </div>
      </div>

      {/* ===== Floating AI assistant launcher ===== */}
      {!realOpen && (
      <div className="flex flex-col items-end" style={{ position: "fixed", bottom: 24, right: 24, zIndex: 50, gap: 12 }}>
        {!introDismissed && !assistantOpen && (
          <div className="up-anim" style={{ position: "relative", width: 268, padding: "15px 16px", background: "var(--card)", border: "1px solid var(--border)", borderRadius: 14, boxShadow: "var(--elevation-lg)", animation: "upIntro 280ms var(--ease-standard)" }}>
            <button onClick={() => setIntroDismissed(true)} className="inline-flex items-center justify-center" style={{ position: "absolute", top: 9, right: 9, width: 22, height: 22, borderRadius: 6, background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)" }}>
              <X style={{ width: 13, height: 13 }} />
            </button>
            <div className="flex items-start" style={{ gap: 10 }}>
              <span className="inline-flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: "var(--spectral-violet-10)", color: "var(--spectral-violet)", flexShrink: 0 }}>
                <Sparkles style={{ width: 16, height: 16 }} />
              </span>
              <div style={{ paddingRight: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--tx-strong)", marginBottom: 2 }}>Need a hand getting started?</div>
                <div style={{ fontSize: 13, lineHeight: 1.45, color: "var(--muted-foreground)" }}>I can help you pick a brand or choose which AI models to probe.</div>
              </div>
            </div>
          </div>
        )}

        {assistantOpen && (
          <div className="up-anim" style={{ width: 328, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 16, boxShadow: "var(--elevation-lg)", overflow: "hidden", animation: "upFade 220ms var(--ease-standard)" }}>
            <div className="flex items-center" style={{ gap: 11, padding: "16px 16px 14px", borderBottom: "1px solid var(--border)" }}>
              <Orb size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14.5, fontWeight: 600, color: "var(--foreground)", lineHeight: 1.2 }}>Upmode Assistant</div>
                <div className="flex items-center" style={{ gap: 6, marginTop: 2 }}>
                  <span style={{ width: 6, height: 6, borderRadius: 9999, background: "var(--signal-green)", flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>Listening</span>
                </div>
              </div>
              <button onClick={() => setAssistantOpen(false)} className="inline-flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 7, background: "transparent", border: "none", cursor: "pointer", color: "var(--muted-foreground)" }}>
                <X style={{ width: 15, height: 15 }} />
              </button>
            </div>
            <div style={{ padding: 16 }}>
              <div className="uppercase" style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: "var(--muted-foreground)", marginBottom: 10 }}>Suggested</div>
              <div className="flex flex-col" style={{ gap: 8 }}>
                {["Which AI models should I select?", "How specific should my brand search be?", "What happens after I start analysis?"].map((q) => (
                  <button key={q} onClick={() => launchAssistant(q)} className="inline-flex items-center" style={{ gap: 12, width: "100%", textAlign: "left", padding: "11px 12px", borderRadius: 11, background: "var(--card)", border: "1px solid var(--border)", boxShadow: "var(--elevation-sm)", cursor: "pointer" }}>
                    <span className="inline-flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 8, background: "var(--muted)", flexShrink: 0 }}>
                      <MessageSquare style={{ width: 15, height: 15, strokeWidth: 1.85, color: "var(--muted-foreground)" }} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: "var(--foreground)" }}>{q}</span>
                    <ArrowUpRight style={{ width: 14, height: 14, strokeWidth: 1.85, color: "var(--silver-mist)", flexShrink: 0 }} />
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); const v = assistantInput.trim(); if (v) launchAssistant(v); }} className="flex items-center" style={{ marginTop: 14, gap: 8, padding: "7px 7px 7px 13px", border: "1px solid var(--border)", borderRadius: 11 }}>
                <input value={assistantInput} onChange={(e) => setAssistantInput(e.target.value)} placeholder="Ask me anything..." style={{ flex: 1, border: "none", outline: "none", background: "transparent", fontSize: 13.5, fontFamily: "inherit", color: "var(--foreground)", minWidth: 0 }} />
                <button type="submit" className="inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, background: "var(--spectral-violet)", border: "none", cursor: "pointer", color: "#fff" }}>
                  <Send style={{ width: 15, height: 15 }} />
                </button>
              </form>
            </div>
          </div>
        )}

        <button onClick={() => { setAssistantOpen((o) => !o); setIntroDismissed(true); }} className="up-anim inline-flex items-center" style={{ gap: 10, padding: "12px 19px", borderRadius: 9999, background: ORB_GRADIENT, color: "#fff", border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600, animation: "upBreathe 4.5s ease-in-out infinite" }}>
          <Sparkles style={{ width: 18, height: 18 }} />
          Ask Upmode
        </button>
      </div>
      )}

      {realOpen && (
        <AIAssistantSidebar
          phase="configure"
          isCollapsed={false}
          onToggleCollapse={(c) => { if (c) setRealOpen(false); }}
          brandName={brand.trim() || undefined}
          externalQuestion={realQ ?? undefined}
        />
      )}
    </div>
  );
}
